import { formatRelativeTime } from '@/utils';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import type { MenuProps } from 'antd/es/menu';
import React from 'react';

import type { GetPipelineExecutionsResponse } from '@mai/platform-api/src/routes/pipeline-executions.schema';

interface RecentRunsDropdownProps {
  executions: GetPipelineExecutionsResponse['items'] | undefined;
  isLoading?: boolean;
  onViewExecution: (
    execution: GetPipelineExecutionsResponse['items'][0],
  ) => void;
  onViewAll: () => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'failed':
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    case 'running':
      return <LoadingOutlined style={{ color: '#1890ff' }} />;
    case 'pending':
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    default:
      return <ClockCircleOutlined />;
  }
};

export const RecentRunsDropdown: React.FC<RecentRunsDropdownProps> = ({
  executions,
  isLoading,
  onViewExecution,
  onViewAll,
}) => {
  // Show only last 5 executions for dropdown
  const recentExecutions = executions?.slice(0, 5) || [];

  const menuItems: MenuProps['items'] = [];

  if (isLoading) {
    menuItems.push({
      key: 'loading',
      label: (
        <div style={{ padding: '8px 12px', textAlign: 'center' }}>
          <LoadingOutlined /> Loading recent runs...
        </div>
      ),
      disabled: true,
    });
  } else if (recentExecutions.length === 0) {
    menuItems.push({
      key: 'empty',
      label: (
        <div
          style={{ padding: '8px 12px', textAlign: 'center', color: '#999' }}
        >
          No previous executions found
        </div>
      ),
      disabled: true,
    });
  } else {
    // Add execution items
    recentExecutions.forEach((execution) => {
      menuItems.push({
        key: execution.pipelineExecutionId,
        label: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '250px',
            }}
          >
            {getStatusIcon(execution.status)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, marginBottom: '2px' }}>
                {execution.pipelineInputSummary || 'Untitled Run'}
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                {formatRelativeTime(execution.createdAt)}
              </div>
            </div>
          </div>
        ),
        onClick: () => onViewExecution(execution),
      });
    });

    // Add separator and "View All" option
    menuItems.push({ type: 'divider' });
    menuItems.push({
      key: 'view-all',
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <HistoryOutlined />
          View All History
        </div>
      ),
      onClick: onViewAll,
    });
  }

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomRight"
    >
      <Button type="text" size="small">
        <HistoryOutlined /> Recent Runs
      </Button>
    </Dropdown>
  );
};
